import React, { useState, useRef, useCallback, useEffect } from 'react';
import './DesignV6.css';
import { getAllProjects, getProjectImages } from '../config/projects';
import { Layers, Smartphone, Pen, Server, ArrowUpRight, MessageCircle, Mail, MapPin } from 'lucide-react';

const AUTOPLAY_INTERVAL = 2200; // ms per project

const DesignV6 = () => {

  const allProjects = getAllProjects().map(project => ({
    ...project,
    images: getProjectImages(project.folder, project.imageCount)
  }));

  const reversedProjects = [...allProjects].reverse();
  const [activeProject, setActiveProject] = useState(reversedProjects[0]);
  const [activeIdx, setActiveIdx] = useState(0);
  const stripRef = useRef(null);
  const listRef = useRef(null);
  const isPaused = useRef(false);
  const timerRef = useRef(null);

  // Advance to next project
  const advance = useCallback(() => {
    if (isPaused.current) return;
    setActiveIdx(prev => {
      const next = (prev + 1) % reversedProjects.length;
      setActiveProject(reversedProjects[next]);
      // desktop: scroll the active row into view inside the list container
      if (listRef.current) {
        const rows = listRef.current.querySelectorAll('.portfolio-row');
        if (rows[next]) {
          const list = listRef.current;
          const row = rows[next];
          const rowTop = row.offsetTop;
          const rowBottom = rowTop + row.offsetHeight;
          const listScrollTop = list.scrollTop;
          const listHeight = list.clientHeight;
          if (rowBottom > listScrollTop + listHeight) {
            list.scrollTo({ top: rowBottom - listHeight, behavior: 'smooth' });
          } else if (rowTop < listScrollTop) {
            list.scrollTo({ top: rowTop, behavior: 'smooth' });
          }
        }
      }
      // mobile: scroll the swipe strip to the new slide
      if (stripRef.current) {
        stripRef.current.scrollTo({ left: next * stripRef.current.clientWidth, behavior: 'smooth' });
      }
      return next;
    });
  }, [reversedProjects]);

  // Auto-play timer
  useEffect(() => {
    timerRef.current = setInterval(advance, AUTOPLAY_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [advance]);

  const pauseAutoPlay = useCallback(() => { isPaused.current = true; }, []);
  const resumeAutoPlay = useCallback(() => { isPaused.current = false; }, []);

  // Called when user swipes the mobile strip
  const handleStripScroll = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx >= 0 && idx < reversedProjects.length) {
      setActiveIdx(idx);
      setActiveProject(reversedProjects[idx]);
    }
  }, [reversedProjects]);

  // Called when a list row is clicked — scrolls the strip to that project
  const selectProject = useCallback((project, idx) => {
    setActiveProject(project);
    setActiveIdx(idx);
    if (stripRef.current) {
      stripRef.current.scrollTo({ left: idx * stripRef.current.clientWidth, behavior: 'smooth' });
    }
  }, []);

  const features = [
    {
      id: 1,
      title: 'Full-Stack Development',
      description: 'Architecting robust full-stack solutions with React, Next.js, Node.js, Laravel & more. Scalable web applications built for performance.',
      icon: <Layers size={22} strokeWidth={1.5} />
    },
    {
      id: 2,
      title: 'Mobile Development',
      description: 'Cross-platform mobile apps with React Native & Flutter, plus native Android with Kotlin. Quality experiences on every device.',
      icon: <Smartphone size={22} strokeWidth={1.5} />
    },
    {
      id: 3,
      title: 'UI/UX Design',
      description: 'Designing intuitive, pixel-perfect interfaces in Figma. From wireframes to production-ready designs that users love.',
      icon: <Pen size={22} strokeWidth={1.5} />
    },
    {
      id: 4,
      title: 'API & Backend Systems',
      description: 'Scalable REST APIs and backend architecture using Node.js, Express, Laravel & Spring Boot. Secure, reliable, and maintainable.',
      icon: <Server size={22} strokeWidth={1.5} />
    }
  ];

  const skills = {
    'Frontend': ['React', 'Next.js', 'JavaScript', 'HTML', 'CSS', 'Tailwind CSS', 'Material UI'],
    'Backend': ['Node.js', 'Express.js', 'Laravel', 'PHP', 'Spring Boot'],
    'Mobile': ['React Native', 'Flutter', 'Kotlin', 'SwiftUI'],
    'Database': ['MySQL', 'MongoDB', 'Firebase'],
    'Tools': ['Git', 'REST APIs', 'Postman', 'Figma']
  };

  return (
    <div className="design-v6-wrapper">
      {/* Navigation */}
      <nav className="nav-v6">
        <div className="nav-v6-content">
          <div className="nav-v6-logo"  title="Shashank Dewangan">SHASHANK </div>
          <div className="nav-v6-menu">
            <a href="#features" className="nav-v6-link">Services</a>
            <a href="#about" className="nav-v6-link">About</a>
            <a href="#experience" className="nav-v6-link">Experience</a>
            <a href="#work" className="nav-v6-link">Portfolio</a>
            <a href="/invoice-generator" className="nav-v6-link">Invoice</a>
            <a href="#contact" className="nav-v6-link">Connect</a>
          </div>
          <a href="#contact" className="nav-v6-cta">Let's Talk</a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-v6">
        <div className="hero-orb hero-orb-1"></div>
        <div className="hero-orb hero-orb-2"></div>
        <div className="hero-orb hero-orb-3"></div>

        <div className="hero-v6-content">
          <div className="hero-badge">
            <span className="hero-badge-dot"></span>
            Available for projects
          </div>

          <h1 className="hero-v6-title">
            Shashank<br />
            <span className="hero-v6-title-accent">Dewangan</span>
          </h1>

          <p className="hero-v6-role">Full-Stack &amp; Mobile Developer · UI/UX Designer</p>

          <p className="hero-v6-desc">
            Building modern web &amp; mobile applications — from government enterprise systems to freelance products. Clean code, intuitive interfaces, real results.
          </p>

          <div className="hero-v6-stats">
            <div className="hero-stat">
              <span className="hero-stat-value">3+</span>
              <span className="hero-stat-label">Years Exp.</span>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <span className="hero-stat-value">18+</span>
              <span className="hero-stat-label">Projects</span>
            </div>
            <div className="hero-stat-divider"></div>
            <div className="hero-stat">
              <span className="hero-stat-value">CDAC</span>
              <span className="hero-stat-label">Project Engineer</span>
            </div>
          </div>

          <div className="hero-v6-buttons">
            <a href="#work" className="btn-v6 btn-v6-primary">View My Work</a>
            <a href="#contact" className="btn-v6 btn-v6-secondary">Get in Touch</a>
          </div>
        </div>

        <div className="hero-scroll-indicator">
          <div className="hero-scroll-line"></div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-v6" id="features">
        <div className="features-v6-content">
          <div className="features-v6-header">
            <span className="features-eyebrow">What I Do</span>
            <h2 className="features-v6-title-main">Services</h2>
          </div>
          <div className="features-v6-list">
            {features.map((feature, idx) => (
              <div key={feature.id} className="feature-v6-item">
                <span className="feature-v6-num">0{idx + 1}</span>
                <span className="feature-v6-icon">{feature.icon}</span>
                <div className="feature-v6-body">
                  <h3 className="feature-v6-title">{feature.title}</h3>
                  <p className="feature-v6-desc">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="about-v6" id="about">
        <div className="about-v6-content">
          <div className="about-v6-left">
            <span className="about-eyebrow">About Me</span>
            <h2 className="about-v6-heading">
              Full-Stack, Mobile &amp;<br/>
              <span className="about-v6-heading-accent">UI/UX Designer</span>
            </h2>
            <p>
              I'm a Full-Stack &amp; Mobile Application Developer and UI/UX Designer with 3+ years of experience building modern web platforms, mobile applications, and intuitive interfaces. My primary expertise spans React, React Native, Next.js, Node.js, Laravel, PHP, and Figma.
            </p>
            <p>
              Currently working as a Project Engineer at CDAC on the CCTNS project under the Ministry of Home Affairs, developing secure and scalable government applications. Alongside my professional role, I've delivered 18+ freelance projects — from eCommerce platforms and admin dashboards to mobile apps and service-based websites.
            </p>
            <div className="about-v6-links">
              <a href="https://github.com/ShashankD200" className="about-v6-link">GitHub</a>
              <a href="https://www.linkedin.com/in/shashankk2001/" className="about-v6-link">LinkedIn</a>
              <a href="https://www.instagram.com/shashankk_d/" className="about-v6-link">Instagram</a>
            </div>
          </div>
          
          <div className="about-v6-right">
            <h3 className="about-v6-subtitle">Tech Stack</h3>
            <div className="skills-v6-grid">
              {Object.entries(skills).map(([category, items]) => (
                <div key={category} className="skill-v6-group">
                  <h4 className="skill-v6-category">{category}</h4>
                  <div className="skill-v6-items">
                    {items.map((skill, idx) => (
                      <span key={idx} className="skill-v6-item">{skill}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience-v6" id="experience">
        <div className="experience-v6-content">
          <h2 className="section-title-v6">Experience</h2>
          <p className="section-desc-v6">Professional roles and freelance work across government and industry</p>

          <div className="experience-v6-timeline">

            <div className="exp-v6-card">
              <div className="exp-v6-header">
                <div className="exp-v6-left">
                  <span className="exp-v6-company">CDAC — Centre for Development of Advanced Computing</span>
                  <h3 className="exp-v6-role">Project Engineer</h3>
                </div>
                <div className="exp-v6-right">
                  <span className="exp-v6-duration">1 Year · Present</span>
                  <span className="exp-v6-type">Full-time</span>
                </div>
              </div>
              <p className="exp-v6-desc">
                Working on the CCTNS (Crime and Criminal Tracking Network &amp; Systems) project under the Ministry of Home Affairs, Government of India. Developing and maintaining frontend applications using React, enhancing performance and usability of government web systems, and collaborating with cross-functional teams to deliver secure, scalable enterprise solutions.
              </p>
              <div className="exp-v6-tags">
                <span>React</span>
                <span>Government Systems</span>
                <span>Enterprise Applications</span>
                <span>CCTNS</span>
              </div>
            </div>

            <div className="exp-v6-card">
              <div className="exp-v6-header">
                <div className="exp-v6-left">
                  <span className="exp-v6-company">Self-Employed</span>
                  <h3 className="exp-v6-role">Freelance Full-Stack Developer &amp; UI/UX Designer</h3>
                </div>
                <div className="exp-v6-right">
                  <span className="exp-v6-duration">3+ Years</span>
                  <span className="exp-v6-type">Freelance</span>
                </div>
              </div>
              <p className="exp-v6-desc">
                Delivered 18+ web and mobile projects for startups and businesses across various industries. Work spans eCommerce platforms, admin dashboards, service-based websites, mobile applications, and UI/UX design — covering the full development lifecycle from design to deployment.
              </p>
              <div className="exp-v6-tags">
                <span>React</span>
                <span>React Native</span>
                <span>Node.js</span>
                <span>Laravel</span>
                <span>Figma</span>
                <span>18+ Projects</span>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Work Section */}
      <section className="work-v6" id="work">
        <div className="work-v6-content">
          <div className="work-v6-header">
            <span className="work-eyebrow">Selected Work</span>
            <h2 className="section-title-v6">Portfolio</h2>
            <p className="section-desc-v6">18+ projects — web platforms, mobile apps, dashboards &amp; design</p>
          </div>

          {/* Mobile: horizontal swipe strip (hidden on desktop) */}
          <div className="portfolio-mobile-strip-wrap">
            <div
              className="portfolio-mobile-strip"
              ref={stripRef}
              onScroll={handleStripScroll}
            >
              {reversedProjects.map((project) => (
                <div key={project.id} className="portfolio-strip-slide">
                  {project.images && project.images.length > 0 ? (
                    <img
                      src={project.images[0]}
                      alt={project.title}
                      className="portfolio-strip-img"
                    />
                  ) : (
                    <div className="portfolio-strip-placeholder" />
                  )}
                  <div className="portfolio-strip-info">
                    <span className="portfolio-preview-category">{project.category}</span>
                    <h3 className="portfolio-preview-title">{project.title}</h3>
                    <p className="portfolio-preview-desc">{project.description}</p>
                    <div className="portfolio-strip-tech">
                      {project.tech.slice(0, 4).map((t, i) => (
                        <span key={i} className="portfolio-row-tag">{t}</span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Dot indicators */}
            <div className="portfolio-strip-dots">
              {reversedProjects.map((_, i) => (
                <button
                  key={i}
                  className={`portfolio-strip-dot${i === activeIdx ? ' active' : ''}`}
                  onClick={() => selectProject(reversedProjects[i], i)}
                  aria-label={`Go to project ${i + 1}`}
                />
              ))}
            </div>
          </div>

          <div
            className="portfolio-index"
            onMouseEnter={pauseAutoPlay}
            onMouseLeave={resumeAutoPlay}
          >
            {/* Left: numbered list */}
            <div className="portfolio-list" ref={listRef}>
              {reversedProjects.map((project, idx) => (
                <div
                  key={project.id}
                  className={`portfolio-row${activeProject.id === project.id ? ' is-active' : ''}`}
                  onMouseEnter={() => { setActiveProject(project); setActiveIdx(idx); }}
                  onClick={() => selectProject(project, idx)}
                >
                  <span className="portfolio-row-num">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <div className="portfolio-row-body">
                    <span className="portfolio-row-title">{project.title}</span>
                    <span className="portfolio-row-meta">{project.category}</span>
                  </div>
                  <div className="portfolio-row-tags">
                    {project.tech.slice(0, 3).map((t, i) => (
                      <span key={i} className="portfolio-row-tag">{t}</span>
                    ))}
                  </div>
                  <span className="portfolio-row-arrow"><ArrowUpRight size={16} strokeWidth={1.75} /></span>
                  {/* Progress bar — animates on active row */}
                  <div className="portfolio-row-progress" />
                </div>
              ))}
            </div>

            {/* Right: sticky preview (desktop only) */}
            <div className="portfolio-preview">
              <div className="portfolio-preview-inner">
                {activeProject.images && activeProject.images.length > 0 ? (
                  <img
                    key={activeProject.id}
                    src={activeProject.images[0]}
                    alt={activeProject.title}
                    className="portfolio-preview-img"
                  />
                ) : (
                  <div className="portfolio-preview-placeholder" />
                )}
                <div className="portfolio-preview-info">
                  <span className="portfolio-preview-category">{activeProject.category}</span>
                  <h3 className="portfolio-preview-title">{activeProject.title}</h3>
                  <p className="portfolio-preview-desc">{activeProject.description}</p>
                  <div className="portfolio-preview-tech">
                    {activeProject.tech.slice(0, 5).map((t, i) => (
                      <span key={i} className="portfolio-row-tag">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-v6">
        <div className="cta-v6-content">
          <span className="cta-eyebrow">Ready to build?</span>
          <h2>Let's Build<br/>Something<br/>Great</h2>
          <p>Whether you need a web application, mobile app, or a custom backend system — I'm ready to bring your vision to life. 3+ years of expertise, 18+ projects delivered.</p>
          <a href="#contact" className="btn-v6 btn-v6-primary btn-v6-large">Start Your Project</a>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-v6" id="contact">
        <div className="contact-v6-content">
          <span className="contact-eyebrow">Get In Touch</span>
          <h2 className="section-title-v6">Let's Connect</h2>
          <p className="section-desc-v6">Open to new projects, collaborations, and opportunities.</p>
          <div className="contact-v6-methods">
            <div className="contact-v6-method">
              <div className="contact-v6-icon"><MessageCircle size={22} strokeWidth={1.5} /></div>
              <h4>WhatsApp</h4>
              <a href="https://wa.me/07725008610">+91 7725008610</a>
            </div>
            <div className="contact-v6-method">
              <div className="contact-v6-icon"><Mail size={22} strokeWidth={1.5} /></div>
              <h4>Email</h4>
              <a href="mailto:contact@shashankdewangan.com">contact@shashankdewangan.com</a>
            </div>
            <div className="contact-v6-method">
              <div className="contact-v6-icon"><MapPin size={22} strokeWidth={1.5} /></div>
              <h4>Location</h4>
              <p>Raipur, India</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer-v6">
        <span className="footer-name">Shashank Dewangan</span>
        <span className="footer-copy">© 2025 · Full-Stack &amp; Mobile Developer</span>
        <div className="footer-links">
          <a href="https://github.com/ShashankD200" target="_blank" rel="noreferrer">GitHub</a>
          <a href="https://www.linkedin.com/in/shashankk2001/" target="_blank" rel="noreferrer">LinkedIn</a>
        </div>
      </footer>
    </div>
  );
};

export default DesignV6;
